import { Link } from "react-router";
import {
  Mail,
  MessageSquare,
  MessageCircle,
  Send,
  Image,
  Globe,
  Phone,
} from "lucide-react";
import { useApi } from "@/hooks/use-api";

interface ChannelInfo {
  id: string;
  name: string;
  status: "connected" | "not_configured" | "unavailable";
}

interface ChannelStatusResponse {
  channels: Array<{
    id: string;
    name: string;
    status: "active" | "error" | "not_configured";
    enabled: boolean;
  }>;
}

function normalizeChannelId(id: string): string {
  if (id === "facebook_messenger") {
    return "messenger";
  }

  if (id === "instagram_dm") {
    return "instagram";
  }

  return id;
}

const CHANNEL_DEFINITIONS = [
  { id: "email", name: "Email", icon: Mail, path: "/settings/messaging/email" },
  { id: "sms", name: "SMS", icon: MessageSquare, path: "/settings/messaging/sms" },
  { id: "whatsapp", name: "WhatsApp", icon: MessageCircle, path: "/settings/messaging/whatsapp" },
  { id: "telegram", name: "Telegram", icon: Send, path: "/settings/messaging/telegram" },
  { id: "instagram", name: "Instagram DM", icon: Image, path: "/settings/messaging/instagram" },
  { id: "messenger", name: "Facebook Messenger", icon: Globe, path: "/settings/messaging/messenger" },
  { id: "viber", name: "Viber", icon: Phone, path: "/settings/messaging/viber" },
] as const;

const DEFAULT_CHANNELS: ChannelInfo[] = CHANNEL_DEFINITIONS.map((ch) => ({
  id: ch.id,
  name: ch.name,
  status: "unavailable",
}));

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-lg border border-border bg-background p-5">
      <div className="mb-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-md bg-muted" />
        <div className="space-y-1.5">
          <div className="h-4 w-24 rounded bg-muted" />
          <div className="h-3 w-20 rounded bg-muted" />
        </div>
      </div>
      <div className="h-8 w-full rounded bg-muted" />
    </div>
  );
}

function StatusBadge({ status }: { status: ChannelInfo["status"] }) {
  const isConnected = status === "connected";
  const isUnavailable = status === "unavailable";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
        isConnected
          ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
          : isUnavailable
            ? "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
            : "bg-muted text-muted-foreground"
      }`}
    >
      {isConnected ? "Connected" : isUnavailable ? "Status Unavailable" : "Not Configured"}
    </span>
  );
}

export default function ChannelManagement() {
  const { data, isLoading, error } = useApi<ChannelStatusResponse>(
    "/api/messaging/channels",
  );

  const channels = data?.channels?.map((channel) => ({
    id: normalizeChannelId(channel.id),
    name: channel.name,
    status: channel.enabled && channel.status === "active"
      ? "connected"
      : channel.status === "not_configured"
        ? "not_configured"
        : "unavailable",
  } satisfies ChannelInfo)) ?? DEFAULT_CHANNELS;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">
          Messaging Channels
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure and manage your communication channels
        </p>
      </div>

      {error ? (
        <div className="rounded-lg border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-4 text-sm text-[var(--color-warning)]">
          Channel status is currently unavailable. You can still open each channel settings page and configure providers directly.
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading
          ? Array.from({ length: 7 }).map((_, i) => <SkeletonCard key={i} />)
          : channels.map((ch) => {
              const def = CHANNEL_DEFINITIONS.find((d) => d.id === ch.id);
              if (!def) return null;
              const Icon = def.icon;

              return (
                <div
                  key={ch.id}
                  className="rounded-lg border border-border bg-background p-5 transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
                        <Icon className="h-5 w-5 text-foreground" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{ch.name}</p>
                        <StatusBadge status={ch.status} />
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <Link
                      to={def.path}
                      className="inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      Configure
                    </Link>
                  </div>
                </div>
              );
            })}
      </div>
    </div>
  );
}
